// Copyright (c) 2024, WSO2 LLC. (https://www.wso2.com). All Rights Reserved.
//
// This software is the property of WSO2 LLC. and its suppliers, if any.
// Dissemination of any information or reproduction of any material contained
// herein in any form is strictly forbidden, unless permitted by WSO2 expressly.
// You may not alter or remove any copyright or other notice from copies of this content.

import React, { useEffect, useState } from 'react';
import {
   Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Select, MenuItem, FormControl, Typography, Box,
   InputLabel,
   SelectChangeEvent,
   Grid,
   InputBase,
   Button,
   TooltipProps,
   Tooltip,
   tooltipClasses
} from '@mui/material';
import * as Types from '../data/utils/types';
import '../styles/ScheduleTable.css';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import styled from '@emotion/styled';
import { blue } from '@mui/material/colors';
import { useParams } from 'react-router-dom';
import DOMPurify from 'dompurify';
import { RESOURCE_SERVER_URLS } from '../data/configs/spl-config';
import { useGetApi } from '../data/hooks/api';
import LinearLoading from '../components/LinearLoading';
import { ItemNotFoundComponent } from './ItemNotFound';
import { ErrorComponent } from './ErrorPage';

type RouteParams = {
   sysId: string;
}

enum eventTypes {
   default = 'ops_default',
   engagement = 'engagement',
   evening = 'ops_evening',
   exclude = 'exclude',
   time_off = 'time_off',
   time_off_evening = 'time_off_evening',
   time_off_morning = 'time_off_morning',
   morning = 'ops_morning',
   night = 'ops_night',
   weekend_night = 'ops_weekend_night',
   weekend = 'ops_weekend'
}

const ScheduleTable: React.FC = () => {

   const { sysId } = useParams<RouteParams>();
   const [teamId, setTeamId] = useState(sysId ? DOMPurify.sanitize(sysId) : "");
   const [duration, setDuration] = useState('');
   const [from, setFrom] = useState<string>(new Date().toLocaleDateString('en-CA'));
   const [eventType, setEventType] = useState('');

   const apiUrl = RESOURCE_SERVER_URLS[0] + `/abt-team-schedule?teamId=${teamId}&from=${from}&duration=${duration}&eventType=${eventType}`;
   const { data, loading, error, getApiData } = useGetApi<Types.ABTTeamScheduleData>(
      { url: apiUrl, headers: { "accept": "application/json" } }
   );

   const serviceNowUrl = data?.snURL + teamId;

   const getBackgroundColor = (eventType: string) => {
      switch (eventType) {
         case eventTypes.default:
            return "#F0EAE6";
         case eventTypes.engagement:
            return "#E6F0EA";
         case eventTypes.evening:
            return "#E1F8DC";
         case eventTypes.exclude:
         case eventTypes.time_off:
         case eventTypes.time_off_evening:
         case eventTypes.time_off_morning:
            return "#F7D8BA";
         case eventTypes.morning:
            return "#ACDDDE";
         case eventTypes.night:
         case eventTypes.weekend_night:
            return "#CAF1DE";
         case eventTypes.weekend:
            return "#FFE7C7";
         default:
            return "#F0EAE6";
      }
   };

   useEffect(() => {
      getApiData(apiUrl);
   }, [teamId, duration, eventType, from])

   const formatDates = (dates: string[]) => {

      return dates
         .map(date => dayjs(date).format('ddd, D MMM'))
   };

   const getUniqueDates = (list: Types.ABTTeamScheduleList[]) => {
      const datesSet = new Set<string>();

      list.forEach(listItem => {
         listItem.members.forEach(member => {
            Object.keys(member.schedule).forEach(date => {
               datesSet.add(date);
            });
         });
      });
      return Array.from(datesSet);
   };

   const uniqueDates = getUniqueDates(data?.list || []);
   const headerDates = formatDates(uniqueDates);

   const handleFromChange = (newValue: Dayjs | null) => {
      if (newValue) {
         setFrom(newValue.format('YYYY-MM-DD'));
      }
   };

   const handleSNUrlClick = () => {
      window.open(serviceNowUrl, '_blank');
   }

   const BlackTooltip = styled(({ className, ...props }: TooltipProps) => (
      <Tooltip {...props} arrow classes={{ popper: className }} />
   ))(({ theme }) => ({
      [`& .${tooltipClasses.arrow}`]: {
         color: "#212A30",
      },
      [`& .${tooltipClasses.tooltip}`]: {
         backgroundColor: "#212A30",
         fontSize: "12px",
      },
   }));

   const MainView = () => {
      return (
         <>
            <TableContainer component={Paper} className="schedule-table-container">
               <Table stickyHeader>
                  <TableHead className="schedule-table-header">
                     <TableRow>
                        <TableCell className="schedule-header-cell">Team</TableCell>
                        <TableCell className="schedule-header-cell">Member</TableCell>
                        {headerDates.map((day, index) => (
                           <TableCell key={index} className="schedule-header-cell">
                              {day}
                           </TableCell>
                        ))}
                     </TableRow>
                  </TableHead>
                  <TableBody>
                     {data?.list.map((team, teamIndex) => (
                        <React.Fragment key={teamIndex}>
                           {team.members.map((member, memberIndex) => (
                              <TableRow key={memberIndex}>
                                 {memberIndex === 0 && (
                                    <TableCell
                                       rowSpan={team.members.length}
                                       className="schedule-team-cell schedule-sticky-column"
                                    >
                                       {team.label}
                                    </TableCell>
                                 )}
                                 <TableCell className='schedule-team-cell schedule-sticky-column'>
                                    {member.name}
                                    {member.roles[0] && (
                                       <span
                                          style={{
                                             marginLeft: '8px',
                                             padding: '3px 8px',
                                             backgroundColor: '#e0f7fa',
                                             borderRadius: '10px',
                                             fontSize: '0.8rem',
                                             fontWeight: 'bold',
                                             color: '#036300',
                                          }}
                                       >
                                          {(member.roles[0]?.label)}
                                       </span>
                                    )}
                                 </TableCell>
                                 {uniqueDates.map((day, dayIndex) => (
                                    <TableCell key={dayIndex} className="schedule-table-cell">
                                       {member.schedule[day] ? (
                                          member.schedule[day].map((schedule, scheduleIndex) => (
                                             <span
                                                key={scheduleIndex}
                                                style={{
                                                   marginLeft: '8px',
                                                   padding: '4px 40px',
                                                   backgroundColor: getBackgroundColor(schedule.name),
                                                   borderRadius: '10px',
                                                   fontSize: '0.85rem',
                                                   color: 'black',
                                                   display: 'inline-block',
                                                   marginBottom: '4px'
                                                }}
                                             >
                                                {schedule.label}
                                             </span>
                                          ))
                                       ) : (
                                          <span
                                             style={{
                                                marginLeft: '8px',
                                                padding: '4px 40px',
                                                backgroundColor: '#F0EAE6',
                                                borderRadius: '10px',
                                                fontSize: '0.85rem',
                                                color: 'black'
                                             }}
                                          >
                                             Default
                                          </span>
                                       )}
                                    </TableCell>
                                 ))}
                              </TableRow>
                           ))}
                        </React.Fragment>
                     ))}
                  </TableBody>
               </Table>
            </TableContainer>
         </>
      )
   }


   return (
      <Box className="schedule-app-container">
         <Box sx={{ display: 'flex', gap: 4, alignItems: 'center', margin: '15px' }}>

            <Box sx={{ display: 'flex', gap: 4, alignItems: 'center', margin: '15px', width: '70%' }}>

               <FormControl sx={{ minWidth: 120 }} >
                  <InputLabel id="demo-select-small-label" sx={{ fontWeight: 'bold' }}>Team</InputLabel>
                  <Select
                     labelId="demo-select-small-label"
                     id="demo-select-small"
                     value={teamId}
                     label="Team"
                     onChange={(e) => setTeamId(e.target.value)}
                     autoWidth
                  >
                     <MenuItem value="">
                        <em>All</em>
                     </MenuItem>
                     {data?.metadata[0].teams.map((team, index) => (
                        <MenuItem value={team.id} key={index}>
                           {team.label}
                        </MenuItem>
                     ))}
                  </Select>
               </FormControl>

               <FormControl sx={{ minWidth: 180 }} >
                  <InputLabel id="demo-select-small-label" sx={{ fontWeight: 'bold' }}>Allocation Type</InputLabel>
                  <Select
                     labelId="demo-select-small-label"
                     id="demo-select-small"
                     value={eventType}
                     label="Allocation Type"
                     onChange={(e) => setEventType(e.target.value)}
                     autoWidth
                  >
                     <MenuItem value="">
                        <em>None</em>
                     </MenuItem>
                     {data?.metadata[0].eventTypes.map((event, index) => (
                        <MenuItem value={event.name} key={index}>
                           {event.label}
                        </MenuItem>
                     ))}
                  </Select>
               </FormControl>

               <FormControl sx={{ minWidth: 120 }} >
                  <InputLabel id="demo-select-small-label" sx={{ fontWeight: 'bold' }}>Duration</InputLabel>
                  <Select
                     labelId="demo-select-small-label"
                     id="demo-select-small"
                     value={duration}
                     label="Duration"
                     onChange={(e) => setDuration(e.target.value)}
                     autoWidth
                  >
                     <MenuItem value='1'>1 Week</MenuItem>
                     <MenuItem value='2'>2 Weeks</MenuItem>
                     <MenuItem value='3'>3 Weeks</MenuItem>
                     <MenuItem value='4'>4 Weeks</MenuItem>
                  </Select>
               </FormControl>

               <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                     label="From"
                     value={dayjs(from)}
                     onChange={(newValue) => handleFromChange(newValue)}
                  />
               </LocalizationProvider>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', marginLeft: '35px' }} width='20%'>
               <BlackTooltip title="ServiceNow access is required" placement="top" arrow>
                  <Button
                     size="small"
                     onClick={handleSNUrlClick}
                     sx={{
                        color: '#e96900',
                        fontSize: '15px',
                        marginLeft: '-5px',
                        fontWeight: 'bold',
                        ':hover': {
                           bgcolor: '#e96900',
                           borderColor: 'primary.main',
                           color: 'white',
                        }
                     }}
                  >
                     Open in ServiceNow
                  </Button>
               </BlackTooltip>

            </Box>
         </Box>

         {loading
            ? <LinearLoading />
            : error
               ? error.status == 404
                  ? <ItemNotFoundComponent />
                  : <ErrorComponent />
               : data && MainView()
         }

      </Box>
   );
};

export default ScheduleTable;
